import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Star, Camera, Loader2, CheckCircle2, X, ExternalLink, Send, Copy, Check } from 'lucide-react';

// Same-origin API. NOTE: this page renders OUTSIDE the Clerk-gated app, so no
// auth interceptor is registered here — these calls hit the public endpoints
// (/api/public/reviews*), which stay inert until REVIEWS_PUBLIC_ENABLED is on.
const API = '/api';

const HG_LOGO = 'https://res.cloudinary.com/dflzi5xc3/image/upload/hg-brand/logo-white.png';
// Cloudflare Turnstile site key — inlined at build time (set in the frontend env).
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

// Module-level (stable) layout wrapper. Defining this INSIDE the component would
// make it a new component type on every render — React would remount the whole
// subtree and drop focus from the inputs after each keystroke.
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-hg-bg flex flex-col items-center px-4 py-8 font-sans">
      <img src={HG_LOGO} alt="Henry-Griffitts" className="h-10 mb-6 mt-2" />
      <div className="w-full max-w-md bg-hg-surface border border-hg-border rounded-sm p-6">
        {children}
      </div>
      <p className="text-[10px] text-hg-text-dim mt-6">Henry-Griffitts · Coeur d'Alene, Idaho</p>
    </div>
  );
}

export default function PublicReviewForm() {
  const [params] = useSearchParams();
  // Link contract: ?subject=customer (default) -> a customer reviewing HG;
  //                ?subject=fitter_partner     -> a fitter reviewing HG as a partner.
  const subjectType = (params.get('subject') || '').toLowerCase() === 'fitter_partner'
    ? 'fitter_partner' : 'hg_customer';
  const source = params.get('source') || (subjectType === 'fitter_partner' ? 'fitter_link' : 'direct');
  const token = params.get('token') || null;

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [consentName, setConsentName] = useState(false);
  const [consentPhoto, setConsentPhoto] = useState(false);
  const [consentTestimonial, setConsentTestimonial] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);   // { positive, googleUrl, id } on success
  const [error, setError] = useState('');
  const [notOpen, setNotOpen] = useState(false);

  // 1-3★ private "tell us more" path
  const [followup, setFollowup] = useState('');
  const [followupSent, setFollowupSent] = useState(false);
  const [sendingFollowup, setSendingFollowup] = useState(false);
  // 4-5★ Google handoff: copy the written review so they can paste it on Google.
  const [copied, setCopied] = useState(false);

  // Fires on the "Share on Google" click. Copies the review text (best-effort) —
  // the anchor's default navigation still opens Google even if the copy is blocked.
  const handleShareClick = () => {
    const t = (text || '').trim();
    if (t && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(t).then(() => setCopied(true)).catch(() => { /* graceful: just open Google */ });
    }
  };

  // Abuse hardening: Cloudflare Turnstile token + a honeypot field (bots fill it).
  const [captcha, setCaptcha] = useState('');
  const [website, setWebsite] = useState('');   // honeypot — real users never see it
  const turnstileRef = useRef(null);
  const renderedRef = useRef(false);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const SCRIPT_ID = 'cf-turnstile-script';
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
    let widgetId;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (window.turnstile && turnstileRef.current && !renderedRef.current) {
        renderedRef.current = true;
        widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'dark',
          callback: (tok) => setCaptcha(tok),
          'error-callback': () => setCaptcha(''),
          'expired-callback': () => setCaptcha(''),
        });
      } else if (!window.turnstile) {
        setTimeout(tryRender, 300);
      }
    };
    tryRender();
    return () => {
      cancelled = true;
      try { if (widgetId && window.turnstile) window.turnstile.remove(widgetId); } catch { /* noop */ }
    };
  }, []);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/public/reviews/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoUrl(res.data.url);
    } catch (err) {
      if (err.response?.status === 503) setNotOpen(true);
      else setError(err.response?.data?.detail || 'Could not upload that photo. Try another.');
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!rating) { setError('Please tap a star rating first.'); return; }
    if (TURNSTILE_SITE_KEY && !captcha) { setError('Please complete the verification below.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/reviews`, {
        subject_type: subjectType,
        rating,
        text: text.trim(),
        reviewer_name: name.trim() || null,
        reviewer_email: email.trim() || null,
        photo_url: photoUrl,
        source,
        token,
        turnstile_token: captcha,
        website,  // honeypot
        consent: {
          display_name: consentName,
          use_photo: consentPhoto,
          use_as_testimonial: consentTestimonial,
        },
      });
      setDone({ positive: !!res.data.positive, googleUrl: res.data.google_url || null, id: res.data.id });
    } catch (err) {
      if (err.response?.status === 503) setNotOpen(true);
      else setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const sendFollowup = async () => {
    if (!followup.trim() || !done?.id) return;
    setSendingFollowup(true);
    try {
      await axios.post(`${API}/public/reviews/${done.id}/followup`, { followup: followup.trim(), website });
      setFollowupSent(true);
    } catch {
      setError('Could not send that — please try again.');
    }
    setSendingFollowup(false);
  };

  if (notOpen) {
    return (
      <Shell>
        <div className="text-center py-6">
          <h1 className="font-display text-xl text-hg-text tracking-wider mb-2">Reviews aren't open yet</h1>
          <p className="text-sm text-hg-text-dim">We're putting the finishing touches on this. Please check back soon — thank you!</p>
        </div>
      </Shell>
    );
  }

  if (done) {
    // 4-5★ → thank-you + Google CTA. 1-3★ → private make-it-right path (no Google
    // steering; Google is never blocked, we simply don't surface it here).
    return (
      <Shell>
        <div className="text-center py-4">
          <CheckCircle2 size={40} className="text-hg-green mx-auto mb-3" />
          <h1 className="font-display text-xl text-hg-text tracking-wider mb-2">Thank you!</h1>

          {done.positive ? (
            <>
              <p className="text-sm text-hg-text-dim mb-5">
                We really appreciate you sharing this. If you have a moment, a public Google review helps others find us.
              </p>
              {done.googleUrl && (
                <>
                  <a
                    href={done.googleUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={handleShareClick}
                    data-testid="review-google-cta"
                    className="inline-flex items-center justify-center gap-2 w-full bg-hg-green text-hg-text font-medium text-sm px-5 py-3 rounded-sm hover:bg-hg-green-hover transition-colors"
                  >
                    <Star size={16} /> Share on Google <ExternalLink size={14} />
                  </a>
                  {(text || '').trim() && (
                    <p className={`text-[11px] mt-2 inline-flex items-center justify-center gap-1 ${copied ? 'text-hg-green' : 'text-hg-text-dim'}`}
                      data-testid="review-copy-hint">
                      {copied
                        ? <><Check size={12} /> Copied your review — just paste it on Google.</>
                        : <><Copy size={12} /> We'll copy your review so you can paste it on Google — no retyping.</>}
                    </p>
                  )}
                </>
              )}
            </>
          ) : followupSent ? (
            <p className="text-sm text-hg-text-dim" data-testid="review-followup-sent">
              Thank you — we've got it, and we'll be in touch to make things right.
            </p>
          ) : (
            <div className="text-left" data-testid="review-private-path">
              <p className="text-sm text-hg-text-dim mb-3 text-center">
                Thank you for the honest feedback. We'd genuinely like to make this right — tell us what happened and how we can fix it.
              </p>
              <textarea
                data-testid="review-followup-text"
                value={followup}
                onChange={(e) => setFollowup(e.target.value)}
                rows={4}
                placeholder="What went wrong? (only we see this)"
                className="w-full bg-hg-bg border border-hg-border rounded-sm p-3 text-sm text-hg-text placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green resize-none mb-3"
              />
              <button
                data-testid="review-followup-send"
                onClick={sendFollowup}
                disabled={sendingFollowup || !followup.trim()}
                className="w-full flex items-center justify-center gap-2 bg-hg-green text-hg-text font-medium text-sm px-5 py-3 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
              >
                {sendingFollowup ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send privately
              </button>
            </div>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="font-display text-xl text-hg-text tracking-wider mb-1">
        {subjectType === 'fitter_partner' ? 'Rate your Henry-Griffitts partnership' : 'Share your experience'}
      </h1>
      <p className="text-sm text-hg-text-dim mb-5">
        {subjectType === 'fitter_partner'
          ? 'How has partnering with Henry-Griffitts been for your business?'
          : 'A quick rating helps more than you know.'}
      </p>

      {/* Star picker — large tap targets */}
      <div className="flex justify-center gap-2 mb-6" data-testid="review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`review-star-${n}`}
            onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="p-1.5"
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
          >
            <Star
              size={40}
              strokeWidth={1.5}
              className={(hover || rating) >= n ? 'text-hg-green' : 'text-hg-border'}
              fill={(hover || rating) >= n ? 'currentColor' : 'none'}
            />
          </button>
        ))}
      </div>

      <label className="block text-xs uppercase tracking-wider text-hg-text-dim mb-1.5">Your review</label>
      <textarea
        data-testid="review-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        maxLength={5000}
        placeholder="What stood out? How are the clubs treating you?"
        className="w-full bg-hg-bg border border-hg-border rounded-sm p-3 text-sm text-hg-text placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green resize-none mb-4"
      />

      {/* Optional photo */}
      <div className="mb-4">
        {photoUrl ? (
          <div className="relative inline-block">
            <img src={photoUrl} alt="your upload" className="h-24 w-24 object-cover rounded-sm border border-hg-border" />
            <button onClick={() => setPhotoUrl(null)} className="absolute -top-2 -right-2 bg-hg-surface border border-hg-border rounded-full p-1 text-hg-text-dim hover:text-red-400" aria-label="remove photo">
              <X size={12} />
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green cursor-pointer">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
            {uploading ? 'Uploading…' : 'Add a photo (optional)'}
            <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="review-photo-input" />
          </label>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 mb-4">
        <input
          data-testid="review-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          maxLength={120}
          className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green"
        />
        <input
          data-testid="review-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (optional — only if you'd like us to follow up)"
          maxLength={200}
          className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green"
        />
      </div>

      {/* Consent — all optional */}
      <div className="space-y-2 mb-5">
        <label className="flex items-start gap-2 text-[11px] text-hg-text-dim cursor-pointer">
          <input type="checkbox" checked={consentTestimonial} onChange={(e) => setConsentTestimonial(e.target.checked)} className="mt-0.5 accent-hg-green" />
          <span>You may use my review publicly as a testimonial.</span>
        </label>
        <label className="flex items-start gap-2 text-[11px] text-hg-text-dim cursor-pointer">
          <input type="checkbox" checked={consentName} onChange={(e) => setConsentName(e.target.checked)} className="mt-0.5 accent-hg-green" />
          <span>You may show my name with it.</span>
        </label>
        {photoUrl && (
          <label className="flex items-start gap-2 text-[11px] text-hg-text-dim cursor-pointer">
            <input type="checkbox" checked={consentPhoto} onChange={(e) => setConsentPhoto(e.target.checked)} className="mt-0.5 accent-hg-green" />
            <span>You may show my photo with it.</span>
          </label>
        )}
      </div>

      {/* Honeypot — hidden from real users; bots fill it and get silently dropped. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      />

      {/* Cloudflare Turnstile widget (renders when the site key is configured) */}
      {TURNSTILE_SITE_KEY && <div ref={turnstileRef} className="mb-4" data-testid="review-turnstile" />}

      {error && <div className="text-xs text-red-400 mb-3" data-testid="review-error">{error}</div>}

      <button
        data-testid="review-submit"
        onClick={submit}
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 bg-hg-green text-hg-text font-medium text-sm px-5 py-3 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Star size={16} />}
        {submitting ? 'Sending…' : 'Submit review'}
      </button>
    </Shell>
  );
}
